import { parseTime, Time } from '@internationalized/date'
import clsx from 'clsx'
import React, {
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@/components-latest/primitives/Button'
import Input from '@/components-latest/primitives/Controls/Input'
import Popover, {
  PopoverAnchor,
  PopoverContent
} from '@/components-latest/primitives/Popover'
import Separator from '@/components-latest/primitives/Separator'
import Text from '@/components-latest/primitives/Text'

import Styles from './TimePicker.module.scss'

const MAX_MINUTE = 60
const MAX_HOUR = 24
const SELECT_SIZE = 280

interface ITimePickerProps
  extends Omit<HTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string | null
  onChange: (valueString: string) => void
  defaultValue?: string
  min?: string | null
  max?: string | null
}

const TimePicker = React.forwardRef<HTMLInputElement, ITimePickerProps>(
  (
    {
      defaultValue = new Time().toString(),
      onChange,
      value,
      className,
      min,
      max,
      ...restProps
    },
    ref
  ) => {
    const { t } = useTranslation()

    const [isOpen, setIsOpen] = useState(false)
    const [time, setTime] = useState(parseTime(defaultValue))

    const hourContainerRef = useRef<HTMLDivElement | null>(null)
    const minuteContainerRef = useRef<HTMLDivElement | null>(null)
    const hourRefs = useRef<(HTMLButtonElement | null)[]>(
      new Array(24).fill(null)
    )
    const minuteRefs = useRef<(HTMLButtonElement | null)[]>(
      new Array(60).fill(null)
    )

    const hourSpacerSize = useMemo(() => {
      if (!time.hour || !hourRefs.current[0]?.offsetHeight) return 0

      const optionHeight = hourRefs.current[0]?.offsetHeight
      if (time.hour && optionHeight) {
        const maxVisibleOptions = SELECT_SIZE / optionHeight
        const shouldAddSpacer = time.hour < MAX_HOUR - maxVisibleOptions
        const spaceSize =
          (maxVisibleOptions - (MAX_HOUR - time.hour)) * optionHeight
        return shouldAddSpacer ? 0 : spaceSize
      }
    }, [time.hour])

    const minuteSpacerSize = useMemo(() => {
      if (!time.minute || !minuteRefs.current[0]?.offsetHeight) return 0

      const optionHeight = minuteRefs.current[0]?.offsetHeight
      if (time.minute && optionHeight) {
        const maxVisibleOptions = SELECT_SIZE / optionHeight
        const shouldAddSpacer = time.minute < MAX_MINUTE - maxVisibleOptions
        const spaceSize =
          (maxVisibleOptions - (MAX_MINUTE - time.minute)) * optionHeight
        return shouldAddSpacer ? 0 : spaceSize
      }
    }, [time.minute])

    const scrollMinutes = useCallback(
      (index: number, options?: ScrollIntoViewOptions) => {
        minuteRefs.current[index]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          ...options
        })
      },
      [time.minute]
    )
    const scrollHours = useCallback(
      (index: number, options?: ScrollIntoViewOptions) => {
        hourRefs.current[index]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          ...options
        })
      },
      [time.hour]
    )

    const closePopup = () => {
      setIsOpen(false)
    }
    const openPopup = () => {
      const newTime = value ? parseTime(value) : null
      if (newTime) {
        setTime(newTime)
        scrollMinutes(newTime.minute, { behavior: 'auto' })
        scrollHours(newTime.hour, { behavior: 'auto' })
      }

      setIsOpen(true)
    }

    const handleHourClick = (index: number) => {
      setTime(time.set({ hour: index }))
      scrollHours(index)
    }
    const handleMinuteClick = (index: number) => {
      setTime(time.set({ minute: index }))
      scrollMinutes(index)
    }
    const handleSelect = () => {
      onChange(time.toString().split(':').slice(0, 2).join(':'))
      closePopup()
    }
    const handleOpenChange = (open: boolean) => {
      if (open) return openPopup()
      return closePopup()
    }

    useEffect(() => {
      scrollMinutes(time.minute)
      scrollHours(time.hour)
    }, [scrollHours, scrollMinutes, time.hour, time.minute])

    const shouldDisableOption = (intentionTime: Time) => {
      if (min && intentionTime.compare(parseTime(min)) <= 0) return true
      if (max && intentionTime.compare(parseTime(max)) >= 0) return true

      return false
    }

    return (
      <div className={Styles.timePicker}>
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
          <PopoverAnchor asChild>
            <Input
              readOnly
              onClick={openPopup}
              onFocus={openPopup}
              value={value || '--:--'}
              className={clsx(Styles.input, className)}
              ref={ref}
              {...restProps}
            />
          </PopoverAnchor>
          <PopoverContent side="bottom" className={Styles.content}>
            <Text variant="nextTitle-3" gutter={16}>
              {t('common.time')}
            </Text>
            <div className={Styles.selectsContainer}>
              <div className={Styles.select} ref={hourContainerRef}>
                {new Array(24).fill(null).map((_, index) => (
                  <button
                    key={index}
                    className={Styles.option}
                    disabled={shouldDisableOption(new Time(index, time.minute))}
                    ref={el => {
                      hourRefs.current[index] = el
                    }}
                    onClick={() => handleHourClick(index)}
                    data-selected={time.hour === index}>
                    <Text variant="nextCaption" asChild></Text>
                    <span>{index.toString().padStart(2, '0')}</span>
                  </button>
                ))}
                <div
                  className={Styles.spacer}
                  style={{ height: `${hourSpacerSize}px` }}
                />
              </div>
              <Separator orientation="vertical" className={Styles.separator} />
              <div className={Styles.select} ref={minuteContainerRef}>
                {new Array(60).fill(null).map((_, index) => (
                  <button
                    key={index}
                    className={Styles.option}
                    ref={el => {
                      minuteRefs.current[index] = el
                    }}
                    onClick={() => handleMinuteClick(index)}
                    disabled={shouldDisableOption(new Time(time.hour, index))}
                    data-selected={time.minute === index}>
                    {index.toString().padStart(2, '0')}
                  </button>
                ))}
                <div
                  className={Styles.spacer}
                  style={{ height: `${minuteSpacerSize}px` }}
                />
              </div>
            </div>

            <Button
              variant="branded"
              size="small"
              className={Styles.cta}
              onClick={handleSelect}>
              {t('common.select')}
            </Button>
          </PopoverContent>
        </Popover>
      </div>
    )
  }
)
TimePicker.displayName = 'TimePicker'

export default TimePicker
